import { useEffect, useMemo } from 'react';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  DataReconciliationSidebar,
  DataReconciliationStep,
} from '@/modules/common/components/templates/data-reconciliation';
import { DataReconciliationPageHeader } from '@/modules/common/components/templates/data-reconciliation/components/parts/data-reconciliation-page-header';
import { dataReconciliationStepToRoute } from '@/modules/common/components/templates/data-reconciliation/components/parts/data-reconciliation-sidebar';
import { EntityType } from '@/modules/common/types/entity-type';
import { useIntegrationStatus } from '@/modules/onboarding/integrations-setup/hooks/use-integration-status';
import { AuthenticatedRoutes, DataReconciliationRoutes, SettingsRoutes } from '@/router/routes';

/**
 * Data Reconciliation Page
 * Main wrapper for data reconciliation flow
 * Shows steps sidebar and routes to appropriate step pages based on conflicts
 */
export function DataReconciliationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: integrationStatusData } = useIntegrationStatus();

  // Determine which steps have conflicts and should be shown
  const { unresolvedSteps, resolvedSteps } = useMemo(() => {
    if (!integrationStatusData) {
      return {
        unresolvedSteps: new Set<DataReconciliationStep>(),
        resolvedSteps: new Set<DataReconciliationStep>(),
      };
    }

    const unresolvedSteps = new Set<DataReconciliationStep>();
    const resolvedSteps = new Set<DataReconciliationStep>();

    const entityTypeToStepMap = new Map<EntityType, DataReconciliationStep>([
      [EntityType.User, DataReconciliationStep.Team],
      [EntityType.Client, DataReconciliationStep.Clients],
      [EntityType.Project, DataReconciliationStep.Projects],
    ]);

    for (const conflict of integrationStatusData.conflicts) {
      const step = entityTypeToStepMap.get(conflict.entityType);
      if (!step) continue;

      if (conflict.isResolved) {
        resolvedSteps.add(step);
      } else {
        unresolvedSteps.add(step);
      }
    }

    return { unresolvedSteps, resolvedSteps };
  }, [integrationStatusData]);

  // Determine current step from URL
  const currentStep: DataReconciliationStep | undefined = useMemo(() => {
    if (location.pathname.includes(DataReconciliationRoutes.Team))
      return DataReconciliationStep.Team;
    if (location.pathname.includes(DataReconciliationRoutes.Clients))
      return DataReconciliationStep.Clients;
    if (location.pathname.includes(DataReconciliationRoutes.Projects))
      return DataReconciliationStep.Projects;
    // Default to first available step from the Set
    return Array.from(unresolvedSteps)[0];
  }, [location.pathname, unresolvedSteps]);

  // Redirect to current step if at base reconciliation route
  useEffect(() => {
    if (location.pathname.endsWith(AuthenticatedRoutes.DataReconciliation) && currentStep) {
      navigate(dataReconciliationStepToRoute[currentStep]);
    }
  }, [location.pathname, currentStep, navigate]);

  // Navigate back to settings/integrations
  const handleBack = () => {
    navigate(SettingsRoutes.Integrations);
  };

  useEffect(() => {
    if (unresolvedSteps.size === 0 && integrationStatusData) {
      navigate(SettingsRoutes.Integrations, { replace: true });
    }
  }, [unresolvedSteps, integrationStatusData, navigate]);

  if (!currentStep) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Header with back button and title */}
      <DataReconciliationPageHeader onBack={handleBack} />

      {/* Main content with sidebar and step content */}
      <div className="flex flex-1 overflow-hidden">
        <DataReconciliationSidebar
          currentStep={currentStep}
          unresolvedSteps={unresolvedSteps}
          resolvedSteps={resolvedSteps}
        />

        <div className="relative flex flex-1 flex-col overflow-y-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
